import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import SmartGreeting from "../../components/SmartGreeting";
import KpiCards from "./KpiCards";
import MuroCard from "./MuroCard";
import PulseFeed from "./PulseFeed";
import "./dashboard.css";

const COLOR_FILTERS = [
    { key: "all",   label: "Tutti" },
    { key: "red",   label: "Rossi" },
    { key: "amber", label: "Ambra" },
    { key: "green", label: "Verdi" },
];

export default function StaffDashboard() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [wall, setWall] = useState({ items: [], totals: { green: 0, amber: 0, red: 0 } });
    const [summary, setSummary] = useState(null);
    const [pulse, setPulse] = useState([]);
    const [loadingWall, setLoadingWall] = useState(true);
    const [loadingPulse, setLoadingPulse] = useState(true);
    const [colorFilter, setColorFilter] = useState("all");

    useEffect(() => {
        (async () => {
            try {
                const [w, s] = await Promise.all([
                    api.get("/internal/dashboard/wall"),
                    api.get("/internal/dashboard/summary"),
                ]);
                setWall(w.data);
                setSummary(s.data);
            } finally {
                setLoadingWall(false);
            }
        })();
        (async () => {
            try {
                const r = await api.get("/internal/dashboard/pulse?limit=50");
                setPulse(r.data.items || []);
            } finally {
                setLoadingPulse(false);
            }
        })();
    }, []);

    const filteredItems = useMemo(() => {
        if (colorFilter === "all") return wall.items;
        return wall.items.filter((i) => i.health?.color === colorFilter);
    }, [wall.items, colorFilter]);

    // Metrics per greeting subtitle
    const greetingMetrics = useMemo(() => {
        if (!summary) return null;
        return {
            ticket_open: summary.kpis?.open_tickets?.value || 0,
            scadenze_7d: summary.kpis?.scadenze_7d?.skipped ? 0 : (summary.kpis?.scadenze_7d?.value || 0),
            docs_review: summary.kpis?.docs_needs_review?.skipped ? 0 : (summary.kpis?.docs_needs_review?.value || 0),
        };
    }, [summary]);

    const handleGreetingClick = (kind) => {
        if (kind === "tickets") nav("/app/tickets");
        else if (kind === "scadenze") nav("/app/scadenze");
        else if (kind === "docs") nav("/app/documenti");
    };

    return (
        <div className="enl-dash" data-testid="staff-dashboard-root">
            <SmartGreeting
                user={user}
                metrics={greetingMetrics}
                onNumberClick={handleGreetingClick}
            />

            <KpiCards summary={summary} />

            <div className="enl-two-col" style={{ marginTop: 32 }}>
                <section data-testid="dashboard-wall-panel">
                    <div className="enl-section-title">
                        <h2>Muro Clienti</h2>
                        <div className="filters" data-testid="wall-filters">
                            {COLOR_FILTERS.map((f) => {
                                const count = f.key === "all"
                                    ? wall.items.length
                                    : (wall.totals?.[f.key] ?? 0);
                                return (
                                    <button
                                        key={f.key}
                                        type="button"
                                        className={`chip-filter ${colorFilter === f.key ? "active" : ""}`}
                                        onClick={() => setColorFilter(f.key)}
                                        data-testid={`wall-filter-${f.key}`}
                                    >
                                        {f.label} · {count}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    {loadingWall && <div className="enl-empty">Caricamento clienti…</div>}
                    {!loadingWall && filteredItems.length === 0 && (
                        <div className="enl-empty" data-testid="wall-empty">
                            Nessun cliente in questa categoria.
                        </div>
                    )}
                    <div className="enl-wall-grid" data-testid="wall-grid">
                        {filteredItems.map((it) => (
                            <MuroCard key={it.id} item={it} />
                        ))}
                    </div>

                    {/* Radar Scadenze */}
                    <div className="enl-section-title" style={{ marginTop: 32 }}>
                        <h2>Radar Scadenze</h2>
                        <span className="count">30 GIORNI</span>
                    </div>
                    <div className="enl-radar" data-testid="dashboard-radar-panel">
                        <div className="enl-radar-glyph" aria-hidden>
                            <svg width="56" height="56" viewBox="0 0 56 56">
                                <circle cx="28" cy="28" r="24" fill="none"
                                        stroke="#CBD5E1" strokeWidth="1" strokeDasharray="2 3" />
                                <circle cx="28" cy="28" r="16" fill="none"
                                        stroke="#CBD5E1" strokeWidth="1" strokeDasharray="2 3" />
                                <circle cx="28" cy="28" r="8" fill="none"
                                        stroke="#CBD5E1" strokeWidth="1" strokeDasharray="2 3" />
                                <line x1="28" y1="28" x2="52" y2="28"
                                      stroke="#94A3B8" strokeWidth="1.5" opacity="0.5" />
                            </svg>
                            <div className="ping" />
                        </div>
                        <div className="enl-radar-title">
                            Il radar si accende con le prime scadenze.
                        </div>
                        <div className="enl-radar-sub">Feature Scadenze in arrivo</div>
                    </div>
                </section>

                <PulseFeed items={pulse} loading={loadingPulse} />
            </div>
        </div>
    );
}
